/**
 * ListingCard Component
 * Professional card component for displaying marketplace listings
 * Supports both Grid and List view modes
 *
 * Handles two different data structures:
 * 1. Browse API: { borrower_preview, lead_details, quality, collaboration, metrics }
 * 2. My Listings API: { lead_snapshot, quality_grade, collaboration_commission_percentage }
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import {
    formatLoanAmount,
    formatTimeAgo,
    getLoanProductConfig,
    getGradeConfig,
    isExpiringSoon,
    getDaysUntilExpiry
} from '../../../services/marketplaceService';

// Helper to check if user has already expressed interest
const hasUserExpressedInterest = (listing) => {
    if (listing.user_has_interest === true) return true;
    if (listing.user_has_interest === false) return false;
    if (listing.user_interest_status) return true;
    return listing.has_expressed_interest ||
           listing.interest_status === 'expressed' ||
           (listing.my_interest !== null && listing.my_interest !== undefined);
};

// Helper to check if user is already a collaborator
const isUserCollaborator = (listing) => {
    if (listing.user_is_collaborator === true) return true;
    if (listing.user_is_collaborator === false) return false;
    return listing.is_collaborator ||
           listing.collaboration_status === 'active' ||
           (listing.my_collaboration !== null && listing.my_collaboration !== undefined);
};

// Listing status → PrimeReact severity (owner My-Listings context)
const STATUS_SEVERITY = {
    active: 'success',
    expired: 'danger',
    cancelled: 'secondary',
    closed: 'secondary',
    in_progress: 'warning',
    converted: 'info',
    sold: 'info',
};

const ListingCard = ({
    listing,
    onInterest,
    onView,
    showActions = true,
    isOwner = false,
    viewMode = 'grid',
    className = '',
    // Optional footer-actions render slot. When provided (e.g. My Listings
    // owner kebab), it REPLACES the buyer Enquire/View actions. Receives the
    // listing. Backward-compatible: buyer surfaces pass nothing.
    renderActions = null
}) => {
    const navigate = useNavigate();

    // Get borrower name - handles both formats
    const getBorrowerName = () => {
        if (listing.borrower_preview?.name) return listing.borrower_preview.name;
        const lead = listing.lead_snapshot || listing.lead || {};
        return lead.borrower_name || lead.masked_name || lead.full_name ||
            (lead.first_name || lead.last_name ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim() : 'Unknown');
    };

    // Get loan amount
    const getLoanAmount = () => {
        if (listing.lead_details?.loan_amount_required) return listing.lead_details.loan_amount_required;
        const lead = listing.lead_snapshot || listing.lead || {};
        return lead.loan_amount_required || lead.loan_amount || lead.amount || 0;
    };

    // Get product type
    const getProductType = () => {
        if (listing.lead_details?.product_type) return listing.lead_details.product_type_detailed || listing.lead_details.product_type;
        const lead = listing.lead_snapshot || listing.lead || {};
        return lead.product_type_detailed || lead.product_type || 'personal_loan';
    };

    // Get location
    const getLocation = () => {
        if (listing.lead_details) {
            const { city, state } = listing.lead_details;
            return city && state ? `${city}, ${state}` : city || state || null;
        }
        const lead = listing.lead_snapshot || listing.lead || {};
        return lead.city && lead.state ? `${lead.city}, ${lead.state}` : lead.city || lead.state || null;
    };

    // Get employment type
    const getEmploymentType = () => {
        if (listing.lead_details?.employment_type) return listing.lead_details.employment_type;
        const lead = listing.lead_snapshot || listing.lead || {};
        return lead.employment_type || null;
    };

    // Get quality grade
    const getQualityGrade = () => {
        if (listing.quality?.grade) return listing.quality.grade;
        const lead = listing.lead_snapshot || {};
        return listing.quality_grade || lead.quality_indicators?.quality_grade || 'E';
    };

    // Get commission percentage
    const getCommission = () => {
        if (listing.collaboration?.commission_percentage !== undefined) return listing.collaboration.commission_percentage;
        return listing.collaboration_commission_percentage || 10;
    };

    // Get max collaborators
    const getMaxCollaborators = () => {
        if (listing.collaboration?.max_collaborators !== undefined) return listing.collaboration.max_collaborators;
        return listing.max_collaborators || 1;
    };

    // Get current collaborators
    const getCurrentCollaborators = () => {
        if (listing.collaboration?.current_collaborators !== undefined) return listing.collaboration.current_collaborators;
        return listing.current_collaborators || 0;
    };

    // Get view count
    const getViewCount = () => {
        if (listing.metrics?.view_count !== undefined) return listing.metrics.view_count;
        return listing.view_count || listing.views_count || 0;
    };

    // Get interest count
    const getInterestCount = () => {
        if (listing.metrics?.interest_count !== undefined) return listing.metrics.interest_count;
        return listing.interest_count || 0;
    };

    // Extract values
    const {
        id,
        posted_at,
        created_at,
        expires_at
    } = listing;

    const borrowerName = getBorrowerName();
    const loanAmount = getLoanAmount();
    const productType = getProductType();
    const location = getLocation();
    const employmentType = getEmploymentType();
    const qualityGrade = getQualityGrade();
    const commission = getCommission();
    const maxCollaborators = getMaxCollaborators();
    const currentCollaborators = getCurrentCollaborators();
    const viewCount = getViewCount();
    const interestCount = getInterestCount();

    // Rich decision-card signals (Browse API → lead_details). Each is
    // null-safe so a partial/legacy listing simply hides that stat.
    const ld = listing.lead_details || {};
    const creditScore = ld.credit_score ?? null;
    const creditBand = ld.credit_band ?? null;
    const foir = ld.foir ?? null;
    const eligibleAmount = ld.eligible_amount ?? null;
    const monthlySalary = ld.monthly_salary ?? null;
    const employerCategory = ld.employer_category ?? null;
    const estPayout = ld.est_payout ?? null;
    const qualityScore = listing.quality?.score ?? null;
    // Live doc completeness (present / required) + offers generated
    const documentsPresent = ld.documents_present ?? null;
    const documentsRequired = ld.documents_required ?? null;
    const offersCount = ld.offers_count ?? null;

    // ₹ with Indian digit grouping (precise, for salary / payout)
    const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
    // FOIR health → severity-style label (lower = healthier)
    const foirHealth = foir == null ? null
        : foir <= 40 ? { label: 'Healthy', cls: 'text-green-600' }
        : foir <= 55 ? { label: 'Moderate', cls: 'text-orange-500' }
        : { label: 'High', cls: 'text-red-500' };

    const productConfig = getLoanProductConfig(productType);
    const gradeConfig = getGradeConfig(qualityGrade);
    const daysRemaining = getDaysUntilExpiry(expires_at);
    const expiringSoon = isExpiringSoon(expires_at);
    const slotsAvailable = maxCollaborators - currentCollaborators;
    const isOpen = slotsAvailable > 0;

    // Check if user can express interest (no existing interest and not collaborating)
    const canExpressInterest = !hasUserExpressedInterest(listing) && !isUserCollaborator(listing);

    const handleViewDetails = (e) => {
        if (e) e.stopPropagation();
        if (onView) onView(listing);
        else navigate(`/app/marketplace/listing/${id}`);
    };

    const handleInterest = (e) => {
        e.stopPropagation();
        if (onInterest) onInterest(listing);
    };

    // List View Layout - Professional table-like layout
    if (viewMode === 'list') {
        return (
            <div
                className={`surface-card border-bottom-1 surface-border px-3 py-2 cursor-pointer hover:surface-100 transition-colors transition-duration-150 ${className}`}
                onClick={() => navigate(`/app/marketplace/listing/${id}`)}
            >
                <div className="flex align-items-center">
                    {/* Grade */}
                    <div
                        className="flex align-items-center justify-content-center border-round flex-shrink-0 mr-3"
                        style={{
                            width: '32px',
                            height: '32px',
                            backgroundColor: gradeConfig.bgColor,
                            color: gradeConfig.color,
                            fontWeight: 700,
                            fontSize: '0.875rem'
                        }}
                    >
                        {qualityGrade}
                    </div>

                    {/* Borrower + Product */}
                    <div style={{ width: '200px' }} className="flex-shrink-0">
                        <div className="font-medium text-900 text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                            {borrowerName}
                        </div>
                        <div className="text-xs text-500">{productConfig.label}</div>
                    </div>

                    {/* Amount */}
                    <div style={{ width: '100px' }} className="flex-shrink-0 text-right">
                        <div className="font-bold text-primary text-sm">{formatLoanAmount(loanAmount)}</div>
                    </div>

                    {/* Location */}
                    <div style={{ width: '150px' }} className="flex-shrink-0 px-3 hidden md:block">
                        <div className="text-sm text-600 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                            {location || '-'}
                        </div>
                    </div>

                    {/* Commission - Key metric for comparison */}
                    <div style={{ width: '80px' }} className="flex-shrink-0 text-center">
                        <div className="text-green-600 font-bold">{commission}%</div>
                    </div>

                    {/* Spots */}
                    <div style={{ width: '60px' }} className="flex-shrink-0 text-center hidden lg:block">
                        <div className={`font-semibold text-sm ${isOpen ? 'text-green-600' : 'text-red-500'}`}>
                            {slotsAvailable}/{maxCollaborators}
                        </div>
                    </div>

                    {/* Actions - Interest first, View last */}
                    {showActions && !isOwner && (
                        <div className="flex-shrink-0 ml-auto flex gap-2">
                            {canExpressInterest && (
                                <Button
                                    icon="pi pi-heart"
                                    label="Enquire"
                                    className="p-button-sm"
                                    severity="success"
                                    onClick={handleInterest}
                                />
                            )}
                            <Button
                                icon="pi pi-eye"
                                label="View"
                                className="p-button-sm p-button-outlined"
                                severity="secondary"
                                onClick={handleViewDetails}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Grid View Layout (Default) — rich "decision card": scan the lead's
    // strength (amount · CIBIL · FOIR · eligibility · quality · payout)
    // before opening. Every stat is null-safe and hides if absent.
    return (
        <div
            className={`surface-card border-round border-1 surface-border cursor-pointer hover:shadow-3 transition-all transition-duration-200 h-full flex flex-column overflow-hidden ${className}`}
            onClick={() => navigate(`/app/marketplace/listing/${id}`)}
        >
            {/* Header: product + location · grade + freshness */}
            <div className="flex align-items-start justify-content-between gap-2 p-3 pb-2">
                <div className="flex-1 min-w-0">
                    <div className="flex align-items-center flex-wrap gap-2 mb-1">
                        <Tag value={productConfig.label} icon={`pi ${productConfig.icon}`} severity="info" className="text-xs white-space-nowrap flex-shrink-0" />
                        {isOwner && listing.status && (
                            <Tag
                                value={listing.status.replace(/_/g, ' ')}
                                severity={STATUS_SEVERITY[listing.status] || 'secondary'}
                                className="text-xs capitalize"
                            />
                        )}
                        {listing.updated_since_interest && (
                            <Tag value="Updated" icon="pi pi-sync" severity="success" className="text-xs" />
                        )}
                        {location && (
                            <span className="text-600 text-xs flex align-items-center gap-1 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                <i className="pi pi-map-marker" /> {location}
                            </span>
                        )}
                    </div>
                    <div className="font-semibold text-900 text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {borrowerName}
                    </div>
                    {(employmentType || employerCategory) && (
                        <div className="text-500 text-xs capitalize white-space-nowrap overflow-hidden text-overflow-ellipsis">
                            {[employmentType?.replace(/_/g, ' '), employerCategory].filter(Boolean).join(' · ')}
                        </div>
                    )}
                </div>
                {/* Narrow right column: owner action (small icon) on top,
                    grade badge, freshness — keeps the cluster ~30px wide so
                    the product tag never gets squeezed to two lines. Buyer
                    surfaces have no action here (it stays in the footer). */}
                <div className="flex flex-column align-items-end gap-1 flex-shrink-0">
                    {showActions && renderActions && (
                        <div onClick={(e) => e.stopPropagation()}>
                            {renderActions(listing)}
                        </div>
                    )}
                    <div
                        className="flex align-items-center justify-content-center border-round"
                        style={{ width: '30px', height: '30px', backgroundColor: gradeConfig.bgColor, color: gradeConfig.color, fontWeight: 700, fontSize: '0.85rem' }}
                        aria-label={`Quality grade ${qualityGrade}`}
                    >
                        {qualityGrade}
                    </div>
                    <span className="text-400 text-xs white-space-nowrap">{formatTimeAgo(posted_at || created_at)}</span>
                </div>
            </div>

            {/* Hero stats: Loan · CIBIL · FOIR */}
            <div className="flex surface-50 border-round mx-3 mb-2 text-center">
                <div className="flex-1 py-2 px-1">
                    <div className="text-900 font-bold text-sm white-space-nowrap">{formatLoanAmount(loanAmount)}</div>
                    <div className="text-500 text-xs">Loan amount</div>
                </div>
                <div className="flex-1 py-2 px-1 border-left-1 surface-border">
                    <div className="text-900 font-bold text-sm">{creditScore || '—'}</div>
                    <div className="text-500 text-xs white-space-nowrap overflow-hidden text-overflow-ellipsis">{creditBand || 'CIBIL'}</div>
                </div>
                <div className="flex-1 py-2 px-1 border-left-1 surface-border">
                    <div className={`font-bold text-sm ${foirHealth ? foirHealth.cls : 'text-900'}`}>{foir != null ? `${foir}%` : '—'}</div>
                    <div className="text-500 text-xs">{foirHealth ? foirHealth.label : 'FOIR'}</div>
                </div>
            </div>

            {/* Eligibility + salary */}
            {(eligibleAmount != null || monthlySalary) && (
                <div className="px-3 flex justify-content-between flex-wrap gap-2 text-xs text-600 mb-2">
                    {eligibleAmount != null && (
                        <span className="flex align-items-center gap-1">
                            <i className="pi pi-check-circle text-green-500" /> Eligible {formatLoanAmount(eligibleAmount)}
                        </span>
                    )}
                    {monthlySalary && (
                        <span className="flex align-items-center gap-1">
                            <i className="pi pi-wallet text-500" /> {inr(monthlySalary)}/mo
                        </span>
                    )}
                </div>
            )}

            {/* Quality bar */}
            {qualityScore != null && (
                <div className="px-3 mb-2">
                    <div className="flex justify-content-between text-xs mb-1">
                        <span className="text-500">Lead quality</span>
                        <span className="text-700 font-medium">{qualityScore}/100 · {qualityGrade}</span>
                    </div>
                    <ProgressBar value={qualityScore} showValue={false} style={{ height: '6px' }} color={gradeConfig.color} />
                </div>
            )}

            {/* Meta: documents + offers as calm neutral chips (identified by
                icon + boxed shape, not loud colour). Green appears only when
                docs are fully complete — i.e. when it actually means
                something. Enquiries muted; expiry is the one warning colour. */}
            <div className="px-3 flex align-items-center flex-wrap gap-2 text-xs mb-2">
                {(documentsRequired != null || documentsPresent != null) && (
                    <span className={`inline-flex align-items-center gap-1 surface-100 border-round px-2 py-1 font-medium ${documentsRequired != null && documentsPresent >= documentsRequired ? 'text-green-600' : 'text-700'}`}>
                        <i className="pi pi-file text-500" />
                        {documentsRequired != null ? `${documentsPresent}/${documentsRequired} docs` : `${documentsPresent} docs`}
                    </span>
                )}
                {offersCount > 0 && (
                    <span className="inline-flex align-items-center gap-1 surface-100 border-round px-2 py-1 text-700 font-medium">
                        <i className="pi pi-tag text-500" />{offersCount} offers
                    </span>
                )}
                {interestCount > 0 && (
                    <span className="text-500 flex align-items-center gap-1"><i className="pi pi-heart-fill" />{interestCount}</span>
                )}
                {expiringSoon && daysRemaining > 0 && (
                    <span className="text-orange-600 ml-auto flex align-items-center gap-1"><i className="pi pi-clock" />{daysRemaining}d left</span>
                )}
            </div>

            {/* Footer: payout strip. Buyer surfaces show Enquire/View here;
                owner actions live top-right (kebab) — no lone bottom bar. */}
            <div className="mt-auto p-3 pt-2 border-top-1 surface-border">
                <div className="flex align-items-center justify-content-between gap-2">
                    <span className="text-green-600 font-bold text-sm white-space-nowrap">{commission}% commission</span>
                    {estPayout != null && (
                        <span className="text-500 text-xs white-space-nowrap">Earn ~{inr(estPayout)}</span>
                    )}
                </div>
                {showActions && !renderActions && !isOwner && (
                    <div className="flex gap-2 mt-2">
                        {canExpressInterest && (
                            <Button icon="pi pi-heart" label="Enquire" severity="success" size="small" className="flex-1" onClick={handleInterest} />
                        )}
                        <Button icon="pi pi-arrow-right" iconPos="right" label="View" outlined severity="secondary" size="small" className={canExpressInterest ? 'flex-1' : 'w-full'} onClick={handleViewDetails} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ListingCard;
